import React from 'react';
import { Container } from '@/components/layout/Container';
import { Section, SectionHeading } from '@/components/layout/Section';
import { Reveal } from '@/components/layout/Reveal';
import { CtaButton } from '@/components/site/CtaButton';
import { WhatsAppButton } from '@/components/site/WhatsAppButton';
import { PhotoFrame } from '@/components/site/PhotoFrame';
import { CtaBlock } from '@/components/site/CtaBlock';
import { CONSULTATION_PATH, IMAGES } from '@/lib/site';
import { useI18n } from '@/lib/i18n';

export default function About() {
  const { t } = useI18n();
  const p = t.aboutPage;

  return (
    <>
      {/* -------------------------------- hero --------------------------------- */}
      <section
        data-testid="about-hero"
        className="relative overflow-hidden border-b border-[color:var(--rmh-border)]"
        style={{ backgroundColor: 'var(--rmh-canvas)' }}
      >
        <span
          aria-hidden="true"
          className="rmh-champagne-wash pointer-events-none absolute inset-0"
        />
        <Container wide className="relative z-10 py-14 lg:py-20">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <div className="mx-auto max-w-[420px] lg:max-w-none">
                <PhotoFrame
                  src={IMAGES.authorityPortrait}
                  webp={IMAGES.authorityPortraitWebp}
                  alt={t.photo.aboutHeroAlt}
                  ratio="4 / 5"
                  objectPosition="50% 30%"
                  caption={t.photo.aboutHeroCaption}
                  eager
                />
              </div>
            </Reveal>

            <div className="lg:col-span-7">
              <Reveal delay={0.08}>
                <div className="flex items-center gap-3">
                  <span
                    className="block h-px w-10"
                    style={{ backgroundColor: 'var(--rmh-accent)' }}
                    aria-hidden="true"
                  />
                  <span className="rmh-eyebrow">{p.hero.eyebrow}</span>
                </div>
                <h1
                  data-testid="about-hero-title"
                  className="rmh-display mt-7 text-[2.2rem] sm:text-[2.9rem] lg:text-[3.7rem]"
                >
                  {p.hero.titleLines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </h1>
                <p className="rmh-body mt-7 max-w-xl text-[0.98rem] sm:text-[1.04rem]">
                  {p.hero.lede}
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <CtaButton
                    to={CONSULTATION_PATH}
                    size="lg"
                    variant="primary"
                    arrow
                    testId="about-hero-book-consultation-button"
                    className="w-full sm:w-auto"
                  >
                    {t.common.bookConsultation}
                  </CtaButton>
                  <WhatsAppButton
                    variant="outline"
                    size="lg"
                    long
                    testId="about-hero-whatsapp-link"
                    className="w-full sm:w-auto"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </section>

      {/* -------------------------------- story --------------------------------- */}
      <Section tone="canvas" wide data-testid="about-story-section">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionHeading eyebrow={p.story.eyebrow} title={p.story.title} />
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-7 flex flex-col gap-5">
                <p className="rmh-body text-[0.96rem] sm:text-[1.02rem]">{p.story.p1}</p>
                <p className="rmh-body text-[0.96rem] sm:text-[1.02rem]">{p.story.p2}</p>
                <p className="rmh-body text-[0.96rem] sm:text-[1.02rem]">{p.story.p3}</p>
                <blockquote
                  className="mt-3 border-l-2 pl-5"
                  style={{ borderColor: 'var(--rmh-accent)' }}
                >
                  <p className="font-serif text-[1.16rem] leading-snug text-[color:var(--rmh-ink)] sm:text-[1.34rem]">
                    {p.story.quote}
                  </p>
                  <cite className="mt-4 block text-[0.76rem] not-italic uppercase tracking-[0.16em] text-[color:var(--rmh-muted)]">
                    {p.story.cite}
                  </cite>
                </blockquote>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.14} className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <PhotoFrame
                src={IMAGES.aboutPortrait}
                webp={IMAGES.aboutPortraitWebp}
                alt={t.photo.aboutPortraitAlt}
                ratio="4 / 5"
                objectPosition="50% 22%"
              />
              <div
                className="mt-6 rounded-[16px] border p-6"
                style={{
                  backgroundColor: 'var(--rmh-surface)',
                  borderColor: 'var(--rmh-border)',
                  boxShadow: 'var(--shadow-hairline)',
                }}
              >
                <p className="rmh-eyebrow text-[0.56rem]">{p.story.numbersLabel}</p>
                <div className="mt-5 flex flex-col gap-5">
                  {t.stats.map((s) => (
                    <div key={s.testId}>
                      <p className="rmh-num text-[1.5rem] leading-none text-[color:var(--rmh-ink)]">
                        {s.value}
                      </p>
                      <p className="mt-2 text-[0.8rem] text-[color:var(--rmh-muted)]">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ------------------------------ philosophy ------------------------------ */}
      <Section tone="canvas2" wide data-testid="about-philosophy-section">
        <Reveal>
          <SectionHeading eyebrow={p.philosophy.eyebrow} title={p.philosophy.title} />
        </Reveal>

        <div
          className="mt-14 grid grid-cols-1 gap-px sm:grid-cols-2"
          style={{ backgroundColor: 'var(--rmh-border)' }}
        >
          {p.philosophy.items.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.07}>
              <div
                data-testid={`about-philosophy-${i}`}
                className="h-full p-7 sm:p-9"
                style={{ backgroundColor: 'var(--rmh-canvas-2)' }}
              >
                <span className="rmh-num text-[0.7rem] text-[color:var(--rmh-muted-2)]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-5 font-serif text-[1.22rem] leading-snug text-[color:var(--rmh-ink)]">
                  {item.title}
                </h3>
                <p className="rmh-body mt-4 text-[0.9rem]">{item.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* -------------------------------- approach ------------------------------ */}
      <Section tone="canvas" wide data-testid="about-approach-section">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <PhotoFrame
              src={IMAGES.heroPortrait}
              webp={IMAGES.heroPortraitWebp}
              alt={t.photo.aboutApproachAlt}
              ratio="3 / 4"
              objectPosition="50% 45%"
              caption={t.photo.aboutApproachCaption}
            />
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal>
              <SectionHeading
                eyebrow={p.approach.eyebrow}
                title={p.approach.title}
                lede={p.approach.lede}
              />
            </Reveal>
            <Reveal delay={0.08}>
              <ul className="mt-9 flex flex-col">
                {p.approach.items.map((line, i) => (
                  <li
                    key={line}
                    data-testid={`about-expect-${i}`}
                    className="flex items-baseline gap-5 border-b py-5"
                    style={{ borderColor: 'var(--rmh-border)' }}
                  >
                    <span className="rmh-num shrink-0 text-[0.7rem] text-[color:var(--rmh-muted-2)]">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="rmh-body text-[0.94rem]">{line}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="mt-9">
                <CtaButton
                  to={CONSULTATION_PATH}
                  size="lg"
                  variant="primary"
                  arrow
                  testId="about-approach-book-consultation-button"
                >
                  {p.approach.cta}
                </CtaButton>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <CtaBlock
        eyebrow={p.finalCta.eyebrow}
        title={p.finalCta.title}
        body={p.finalCta.body}
        testIdPrefix="about-final-cta"
      />
    </>
  );
}
